// Firestore — Serviço da coleção `user_lists`

import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
  type QuerySnapshot,
  type DocumentSnapshot,
} from "firebase/firestore";
import { createDocSettled, settleWrite } from "../../../lib/firebase/writeSettle";
import { resilientOnSnapshot } from "../../../lib/firebase/resilientSnapshot";
import { withReadTimeout } from "../../../lib/firebase/readTimeout";
import { db } from "../../../lib/firebase/config";

export interface ListBook {
  /**
   * ID original do livro. Pode ser canonical (`isbn:X`, `apple:X`, `google:X`)
   * pra livros adicionados após o refactor Work+Edition (2026-05), ou legacy
   * (`apple-X`, `google-X`, raw Google volume id) pra livros antigos.
   */
  googleBookId: string;
  /**
   * F4-cleanup (2026-05-14): canonical resolvido. Quando presente, o dedup
   * de lista usa ele primeiro (`addBookToList`/`isBookInList`) — evita
   * duplicar o mesmo livro com bookId em formato diferente.
   */
  canonicalBookId?: string | null;
  title: string;
  coverUrl: string;
  author: string;
}

/**
 * Encontra um ListBook na lista que corresponde ao lookupId — matching
 * flexível pra evitar duplicatas quando book ID vem em formato diferente
 * do que foi salvo originalmente.
 *
 * Match em ordem:
 *   1. `googleBookId === lookupId` (caso comum)
 *   2. `canonicalBookId === lookupId` (URL canonical, doc legacy)
 *   3. Normalização hyphen↔colon (apple-X ↔ apple:X)
 *
 * Espelho do `findUserBookByAnyId` mas pra listas.
 */
export function findListBookByAnyId(list: ListBook[], lookupId: string): ListBook | null {
  if (!list.length || !lookupId) return null;
  const direct = list.find((b) => b.googleBookId === lookupId);
  if (direct) return direct;
  const byCanonical = list.find((b) => b.canonicalBookId && b.canonicalBookId === lookupId);
  if (byCanonical) return byCanonical;
  const lookupNorm = lookupId.replace(/^(apple|google)-/, "$1:");
  const lookupHyphen = lookupId.replace(/^(apple|google):/, "$1-");
  return list.find((b) => {
    const idNorm = b.googleBookId.replace(/^(apple|google)-/, "$1:");
    return idNorm === lookupNorm ||
      b.googleBookId === lookupHyphen ||
      b.canonicalBookId === lookupNorm;
  }) ?? null;
}

export interface UserList {
  id: string;
  userId: string;
  name: string;
  /** Lista virtual nativa (estante); não persiste em `user_lists`. */
  isSmart?: boolean;
  description?: string;
  books: ListBook[];
  isPinned?: boolean;
  /** UIDs com permissão de edição (dono em `userId` não precisa repetir aqui). */
  collaborators: string[];
  /** UIDs que salvaram/seguem a lista — visitantes VIP, sem permissão de edição. */
  followers: string[];
  /** Torna a lista visível no Motor de Descoberta. */
  isPublic?: boolean;
  /** URL Cloudinary da capa personalizada. */
  coverUrl: string;
  /** Cor tema da lista (hex), usada em destaques/overlays. */
  themeColor: string;
  /** Contador desnormalizado de seguidores (para orderBy no Explore). */
  followersCount?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export type NewUserList = Pick<UserList, "userId" | "name"> & {
  books?: ListBook[];
  description?: string;
  collaborators?: string[];
  coverUrl?: string;
  themeColor?: string;
  isPublic?: boolean;
};

const LISTS_CACHE_TTL_MS = 5 * 60 * 1000;
const DISCOVER_CACHE_KEY = "livra_cache_discover_lists";
/**
 * `_v2` porque a semântica do payload mudou nesta leva (buffer curto + corte
 * de lista vazia + desempate em memória). Sem trocar a chave, quem tivesse o
 * cache de 5 min da versão anterior continuaria vendo a ordem velha.
 */
const TRENDING_CACHE_KEY = "livra_cache_trending_lists_v2";

/**
 * Quantas listas a vitrine "Em Alta" mostra. Fonte única: a query pede um
 * múltiplo disto.
 *
 * ⚠️ 6 → 8 em 02/09, e o que destravou o número foi a FORMA. Enquanto a
 * vitrine era uma grade de 2 colunas, cada par custava 261px de altura e o
 * teto era de layout: medido, 6 cards davam 801px numa viewport de 844 — a
 * seção inteira era uma tela. Virando rail horizontal, a altura passou a ser
 * constante e a contagem deixou de comprar altura.
 *
 * O que ela ainda compra é LEITURA (`TRENDING_FETCH_LIMIT` abaixo), e é por
 * isso que 8 e não 12: cada documento de lista carrega o array `books`
 * inteiro. Medido em produção em 02/09, o número é folgado de qualquer forma
 * — existem 5 listas públicas no app inteiro (12 no total), então hoje a
 * query lê 5 e mostra 5.
 */
export const TRENDING_DISPLAY_COUNT = 8;

/**
 * Superprovisionamento (mesmo padrão do BUG-178): pedimos mais do que
 * mostramos porque o corte de qualidade acontece DEPOIS da query — lista
 * pública sem livro nenhum não pode ocupar vaga numa vitrine. Sem folga, cada
 * descarte viraria um buraco na vitrine.
 */
const TRENDING_FETCH_LIMIT = TRENDING_DISPLAY_COUNT * 3;

function normalizeList(id: string, raw: Record<string, unknown>): UserList {
  // Backward compat: old docs use bookIds: string[]
  let books: ListBook[] = [];
  if (Array.isArray(raw.books)) {
    books = raw.books as ListBook[];
  } else if (Array.isArray(raw.bookIds)) {
    books = (raw.bookIds as string[]).map((bid) => ({
      googleBookId: bid,
      title: "",
      coverUrl: "",
      author: "",
    }));
  }

  return {
    id,
    userId: String(raw.userId ?? ""),
    name: String(raw.name ?? ""),
    description: raw.description != null ? String(raw.description) : undefined,
    books,
    isPinned: raw.isPinned === true ? true : undefined,
    collaborators: Array.isArray(raw.collaborators) ? (raw.collaborators as string[]) : [],
    followers: Array.isArray(raw.followers) ? (raw.followers as string[]) : [],
    isPublic: raw.isPublic === true ? true : undefined,
    coverUrl: typeof raw.coverUrl === "string" ? raw.coverUrl : "",
    themeColor: typeof raw.themeColor === "string" ? raw.themeColor : "",
    followersCount: typeof raw.followersCount === "number" ? raw.followersCount : undefined,
    createdAt: raw.createdAt as Timestamp | undefined,
    updatedAt: raw.updatedAt as Timestamp | undefined,
  };
}

function readListsCache(key: string): UserList[] | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { timestamp?: number; data?: UserList[] };
    if (!parsed.timestamp || !Array.isArray(parsed.data)) return null;
    if (Date.now() - parsed.timestamp > LISTS_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeListsCache(key: string, data: UserList[]): void {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        timestamp: Date.now(),
        data,
      }),
    );
  } catch {
    // Cache é opcional.
  }
}

export function canEditList(list: UserList, uid: string | undefined): boolean {
  if (list.isSmart) return false;
  if (!uid) return false;
  if (list.userId === uid) return true;
  return list.collaborators.includes(uid);
}

export function isListOwner(list: UserList, uid: string | undefined): boolean {
  return !!uid && list.userId === uid;
}

async function fetchListOrThrow(listId: string): Promise<UserList> {
  const snap = await getDoc(doc(db, "user_lists", listId));
  if (!snap.exists()) throw new Error("Lista não encontrada");
  return normalizeList(snap.id, snap.data() as Record<string, unknown>);
}

async function assertCanEditList(listId: string, editorUid: string): Promise<UserList> {
  const list = await fetchListOrThrow(listId);
  if (!canEditList(list, editorUid)) throw new Error("Sem permissão para editar esta lista");
  return list;
}

async function assertIsOwner(listId: string, uid: string): Promise<UserList> {
  const list = await fetchListOrThrow(listId);
  if (list.userId !== uid) throw new Error("Apenas o dono da lista pode fazer isso");
  return list;
}

export function subscribeToUserLists(
  userId: string,
  callback: (lists: UserList[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, "user_lists"),
    where("userId", "==", userId),
    orderBy("updatedAt", "desc"),
    limit(50),
  );
  return resilientOnSnapshot(
    (next, error) => onSnapshot(q, next, error),
    (snap: QuerySnapshot) => callback(snap.docs.map((d) => normalizeList(d.id, d.data() as Record<string, unknown>))),
    (err) => onError?.(err),
  );
}

export function subscribeToCollaboratorLists(
  userId: string,
  callback: (lists: UserList[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, "user_lists"),
    where("collaborators", "array-contains", userId),
    orderBy("updatedAt", "desc"),
    limit(50),
  );
  return resilientOnSnapshot(
    (next, error) => onSnapshot(q, next, error),
    (snap: QuerySnapshot) => callback(snap.docs.map((d) => normalizeList(d.id, d.data() as Record<string, unknown>))),
    (err) => onError?.(err),
  );
}

/** Listas salvas/seguidas pelo usuário (ele está em `followers`). */
export function subscribeToFollowedLists(
  userId: string,
  callback: (lists: UserList[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, "user_lists"),
    where("followers", "array-contains", userId),
    orderBy("updatedAt", "desc"),
    limit(50),
  );
  return resilientOnSnapshot(
    (next, error) => onSnapshot(q, next, error),
    (snap: QuerySnapshot) => callback(snap.docs.map((d) => normalizeList(d.id, d.data() as Record<string, unknown>))),
    (err) => onError?.(err),
  );
}

/** Alterna o follow (salvar) de uma lista. Followers são visitantes — sem permissão de edição. */
export async function toggleFollowList(listId: string, userId: string): Promise<void> {
  const listSnap = await getDoc(doc(db, "user_lists", listId));
  if (!listSnap.exists()) throw new Error("Lista não encontrada");
  const list = normalizeList(listSnap.id, listSnap.data() as Record<string, unknown>);
  if (list.userId === userId) throw new Error("O dono não pode seguir a própria lista");
  const isFollowing = list.followers.includes(userId);
  await settleWrite(updateDoc(doc(db, "user_lists", listId), {
    followers: isFollowing ? arrayRemove(userId) : arrayUnion(userId),
    followersCount: increment(isFollowing ? -1 : 1),
    updatedAt: serverTimestamp(),
  }), "listsService.toggleFollowList");
}

export function subscribeToListById(
  listId: string,
  callback: (list: UserList | null) => void,
  onError?: (error: Error) => void,
): () => void {
  return resilientOnSnapshot(
    (next, error) => onSnapshot(doc(db, "user_lists", listId), next, error),
    (snap: DocumentSnapshot) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      callback(normalizeList(snap.id, snap.data() as Record<string, unknown>));
    },
    (err) => onError?.(err),
  );
}

export async function createList(data: NewUserList): Promise<string> {
  const payload = {
    userId: data.userId,
    name: data.name,
    ...(data.description != null && data.description !== "" ? { description: data.description } : {}),
    books: data.books ?? [],
    collaborators: data.collaborators ?? [],
    followers: [] as string[],
    followersCount: 0,
    coverUrl: data.coverUrl ?? "",
    themeColor: data.themeColor ?? "",
    isPublic: data.isPublic ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await createDocSettled(collection(db, "user_lists"), payload, "listsService.createList");
  return ref.id;
}

export async function updateListName(listId: string, name: string, editorUid: string): Promise<void> {
  await assertCanEditList(listId, editorUid);
  await settleWrite(updateDoc(doc(db, "user_lists", listId), { name, updatedAt: serverTimestamp() }), "listsService.updateListName");
}

/** Atualiza nome, descrição, capa e/ou visibilidade pública numa única operação. */
export async function updateListMeta(
  listId: string,
  editorUid: string,
  updates: { name?: string; description?: string; coverUrl?: string; isPublic?: boolean },
): Promise<void> {
  await assertCanEditList(listId, editorUid);
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (updates.name !== undefined) patch.name = updates.name;
  if (updates.description !== undefined) patch.description = updates.description;
  if (updates.coverUrl !== undefined) patch.coverUrl = updates.coverUrl;
  if (updates.isPublic !== undefined) patch.isPublic = updates.isPublic;
  await settleWrite(updateDoc(doc(db, "user_lists", listId), patch), "listsService.updateListMeta");
}

export async function addBookToList(listId: string, book: ListBook, editorUid: string): Promise<void> {
  const list = await assertCanEditList(listId, editorUid);
  // F4-cleanup: dedup flexível — match em googleBookId/canonicalBookId/hyphen↔colon.
  if (findListBookByAnyId(list.books, book.googleBookId)) return;
  if (book.canonicalBookId && findListBookByAnyId(list.books, book.canonicalBookId)) return;
  await settleWrite(updateDoc(doc(db, "user_lists", listId), {
    books: arrayUnion(book),
    updatedAt: serverTimestamp(),
  }), "listsService.addBookToList");
}

export async function removeBookFromList(listId: string, googleBookId: string, editorUid: string): Promise<void> {
  const list = await assertCanEditList(listId, editorUid);
  // F4-cleanup: aceita lookupId em qualquer formato — encontra o doc real
  // mesmo se quem chamou usou canonical e o item foi salvo com legacy.
  const target = findListBookByAnyId(list.books, googleBookId);
  const targetId = target?.googleBookId ?? googleBookId;
  const updatedBooks = list.books.filter((b) => b.googleBookId !== targetId);
  await settleWrite(updateDoc(doc(db, "user_lists", listId), {
    books: updatedBooks,
    updatedAt: serverTimestamp(),
  }), "listsService.removeBookFromList");
}

export async function updateListOrder(listId: string, updatedBooks: ListBook[], editorUid: string): Promise<void> {
  await assertCanEditList(listId, editorUid);
  await settleWrite(updateDoc(doc(db, "user_lists", listId), {
    books: updatedBooks,
    updatedAt: serverTimestamp(),
  }), "listsService.updateListOrder");
}

export async function deleteList(listId: string, requesterUid: string): Promise<void> {
  await assertIsOwner(listId, requesterUid);
  await settleWrite(deleteDoc(doc(db, "user_lists", listId)), "listsService.deleteList");
}

export async function updateListAppearance(
  listId: string,
  editorUid: string,
  updates: { coverUrl?: string; themeColor?: string },
): Promise<void> {
  await assertCanEditList(listId, editorUid);
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (updates.coverUrl !== undefined) patch.coverUrl = updates.coverUrl;
  if (updates.themeColor !== undefined) patch.themeColor = updates.themeColor;
  await settleWrite(updateDoc(doc(db, "user_lists", listId), patch), "listsService.updateListAppearance");
}

export async function toggleListPin(listId: string, ownerUid: string, isPinned: boolean): Promise<void> {
  await assertIsOwner(listId, ownerUid);
  await settleWrite(updateDoc(doc(db, "user_lists", listId), { isPinned, updatedAt: serverTimestamp() }), "listsService.toggleListPin");
}

/** Somente o dono pode convidar. */
export async function addCollaboratorToList(
  listId: string,
  ownerUid: string,
  newCollaboratorUid: string,
): Promise<void> {
  const list = await assertIsOwner(listId, ownerUid);
  if (newCollaboratorUid === list.userId) throw new Error("Esse usuário já é o dono da lista");
  if (list.collaborators.includes(newCollaboratorUid)) return;

  await settleWrite(updateDoc(doc(db, "user_lists", listId), {
    collaborators: arrayUnion(newCollaboratorUid),
    updatedAt: serverTimestamp(),
  }), "listsService.addCollaboratorToList");

  // Notificação de convite será enviada via Cloud Function callable `notify` (Sprint D).
  void ownerUid;
}

/** Somente o dono remove colaboradores. */
export async function removeCollaboratorFromList(
  listId: string,
  ownerUid: string,
  collaboratorUid: string,
): Promise<void> {
  await assertIsOwner(listId, ownerUid);
  await settleWrite(updateDoc(doc(db, "user_lists", listId), {
    collaborators: arrayRemove(collaboratorUid),
    updatedAt: serverTimestamp(),
  }), "listsService.removeCollaboratorFromList");
}

/**
 * Listas públicas de um usuário específico — usada no Perfil Público.
 */
export async function getPublicUserLists(userId: string): Promise<UserList[]> {
  const q = query(
    collection(db, "user_lists"),
    where("userId", "==", userId),
    where("isPublic", "==", true),
  );
  const snap = await withReadTimeout(getDocs(q));
  return snap.docs.map((d) => normalizeList(d.id, d.data() as Record<string, unknown>));
}

/**
 * Motor de Descoberta — busca listas públicas (isPublic === true).
 * Limitado a 30 documentos. Filtro de texto feito client-side.
 */
export async function getDiscoverLists(): Promise<UserList[]> {
  const cached = readListsCache(DISCOVER_CACHE_KEY);
  if (cached) return cached;
  const q = query(
    collection(db, "user_lists"),
    where("isPublic", "==", true),
    limit(50),
  );
  const snap = await getDocs(q);
  const data = snap.docs.map((d) => normalizeList(d.id, d.data() as Record<string, unknown>));
  writeListsCache(DISCOVER_CACHE_KEY, data);
  return data;
}

/**
 * Desempate de "Em Alta", em memória, dentro do buffer.
 *
 * ⚠️ Isto existe porque `followersCount` é um contador VITALÍCIO e a base do
 * beta é pequena: quase toda lista pública está em 0, e no empate o Firestore
 * ordena por id do documento. Sem desempate, "Em Alta" é sempre as MESMAS
 * listas, para sempre, independente de qualquer coisa que aconteça no produto
 * — e uma lista que ninguém salvou fica no topo indefinidamente.
 *
 * Entre iguais, ganha a mais recente e depois a mais cheia: é o que faz a
 * vitrine mexer conforme as pessoas curam, sem inventar um sinal que não
 * temos. Salvamentos continuam mandando quando existem.
 */
function compareTrending(a: UserList, b: UserList): number {
  const byFollowers = (b.followersCount ?? 0) - (a.followersCount ?? 0);
  if (byFollowers !== 0) return byFollowers;
  const byRecency = (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0);
  if (byRecency !== 0) return byRecency;
  return b.books.length - a.books.length;
}

/**
 * Listas em Alta — listas públicas mais salvas, para a vitrine da Busca.
 * Requer índice composto no Firestore: isPublic ASC + followersCount DESC.
 *
 * ── Dois limites conhecidos, e nenhum é conserto de código ─────────────────
 * ① `orderBy` do Firestore EXCLUI documento que não tem o campo. Lista criada
 *    antes de `followersCount` existir nunca aparece aqui. O impacto é da
 *    cauda, não do topo: `createList` grava 0 desde sempre e `toggleFollowList`
 *    usa `increment`, que CRIA o campo — então qualquer lista que receba um
 *    salvamento entra na disputa. Quem fica de fora só ficaria em último.
 * ② O ranking é sobre o buffer, não sobre a coleção: acima de
 *    `TRENDING_FETCH_LIMIT` listas públicas empatadas em salvamentos, o pool
 *    que chega já é o recorte do Firestore. Enquanto os salvamentos forem
 *    poucos, "Em Alta" é mais vitrine do que ranking — e é honesto dizer isso
 *    aqui em vez de a tela prometer uma disputa que não existe.
 */
export async function getTrendingLists(): Promise<UserList[]> {
  const cached = readListsCache(TRENDING_CACHE_KEY);
  if (cached) return cached;
  const q = query(
    collection(db, "user_lists"),
    where("isPublic", "==", true),
    orderBy("followersCount", "desc"),
    // Antes eram 50 documentos lidos para exibir 6 — e cada doc de lista
    // carrega o array `books` inteiro (título, capa e autor por livro), então
    // os 44 descartados não eram baratos.
    limit(TRENDING_FETCH_LIMIT),
  );
  const snap = await getDocs(q);
  const data = snap.docs
    .map((d) => normalizeList(d.id, d.data() as Record<string, unknown>))
    // Lista pública vazia desenha mosaico em branco e "0 livros": é a pior
    // vitrine possível e um beco para quem toca.
    .filter((l) => l.books.length > 0)
    .sort(compareTrending)
    .slice(0, TRENDING_DISPLAY_COUNT);
  writeListsCache(TRENDING_CACHE_KEY, data);
  return data;
}
